import { usePermissionCoursesQuery } from '@innovamat/glow-api-client';

import { useUser } from '../../user-management';
import type { CoursePermission } from '../modules/permission/course';
import {
  hasSomePermissionsForAdminDashboard,
  hasSomePermissionsForAssessment,
  hasSomePermissionsForIntervention,
} from '../modules/permission/course';

import { useOrganization } from './use-organization';

type BasePermissions = {
  adminDashboard: boolean;
  assessment: boolean;
  earlyIntervention: boolean;
  platformEnabled: boolean;
  tutoringChat: boolean;
};

type UsePermissions = {
  allPermissions: CoursePermission[] | undefined;
  arePermissionsLoaded: boolean;
  arePermissionsLoadedWithoutCourse: boolean;
  hasPermissionsForAdminDashboard: () => boolean;
  hasPermissionsForAssessment: () => boolean;
  hasPermissionsForIntervention: () => boolean;
  permissionsByCurrentCourse: BasePermissions;
};

type Props = {
  courseId?: string | null | undefined;
};

function usePermissionCourses({ courseId }: Props): UsePermissions {
  const { user } = useUser();
  const { organization, isJourneysEnabled } = useOrganization();
  const organizationId = user?.organizationId ?? '';
  const academicYearId = organization?.academicYearId ?? '';

  const { data, isLoading, isSuccess } = usePermissionCoursesQuery<{
    permissionCourses: CoursePermission[];
  }>(
    {
      academicYearId,
      organizationId,
    },
    {
      enabled: !!organizationId && !!academicYearId,
      staleTime: Infinity,
    }
  );

  const permissionsByCourse = (
    courseId: string | null | undefined
  ): BasePermissions => {
    const permissions = data?.permissionCourses?.find(
      (permission) => permission?.courseId === courseId
    );

    if (!permissions) {
      return {
        adminDashboard: false,
        assessment: false,
        earlyIntervention: false,
        platformEnabled: false,
        tutoringChat: false,
      };
    }

    return {
      adminDashboard: !!permissions.permission?.adminDashboard,
      assessment: !!permissions.permission?.assessment,
      earlyIntervention: !!permissions.permission?.earlyIntervention,
      platformEnabled: !!permissions.permission?.platformEnabled,
      tutoringChat: !!permissions.permission?.tutoringChat,
    };
  };

  return {
    allPermissions: data?.permissionCourses,
    arePermissionsLoaded: Boolean(isSuccess && !isLoading && !!courseId),
    arePermissionsLoadedWithoutCourse: Boolean(isSuccess && !isLoading),
    hasPermissionsForAdminDashboard: () =>
      !isJourneysEnabled &&
      hasSomePermissionsForAdminDashboard(data?.permissionCourses),
    hasPermissionsForAssessment: () =>
      hasSomePermissionsForAssessment(data?.permissionCourses),
    hasPermissionsForIntervention: () =>
      hasSomePermissionsForIntervention(data?.permissionCourses),
    permissionsByCurrentCourse: permissionsByCourse(courseId),
  };
}

export { usePermissionCourses };
